<template>
    <div>
        <h1 class="page-title">{{ title }}</h1>
    </div>
   
        <div data-kt-customer-table-toolbar="base" class="flex justify-end">
             <Button @click="$router.push({ name: 'addVariantProductCollection' })"
                class="button-add"                    
                icon="pi pi-plus"
                aria-label="Ajouter une collection"
                label="Ajouter une collection"/>
        </div>
  
    <div class="card">
        <DataTable
            :value="tableData"
            :rows="rows"
            :totalRecords="totalRecords"
            :currentPage="currentPage"
            :rowHover="true"
            :loading="loading"
            :rowsPerPageOptions="[15, 25, 100]"
            lazy
            paginator
            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Affichage de {first} à {last} sur {totalRecords} entrées"
            @page="onPaginate"
            @sort="onSort($event)"
            removableSort
        >
            <template #header>
                <div class="flex justify-between card-header border-0">
                    <div class="card-title">
                        <div class="my-1">
                            <span class="p-input-icon-left">
                                <i class="pi pi-search" />
                                <InputText type="text" class="form-control w-[250px] ps-15"
                                    v-model="criteria"
                                    @keyup.enter="onFilterDt"
                                    placeholder="Rechercher une catégorie"
                                    @input="onClearInputFilter" />
                            </span>
                        </div>
                    </div>
                    
                </div>
            </template>
            <div class="card-body pt-0">
                <Column field="name" header="Nom de la collection" sortable></Column>
                <Column headerStyle="width: 12em;">
                    <template #header>
                        <div>Actions</div>
                    </template>
                    <template #body="slotProps">
                        <router-link :to="{ name: 'editSocialEventGrantCollection', params: { uuid: slotProps.data.uuid }}">
                            <Button type="button" icon="pi pi-cog" title="Éditer" class="p-edition" style="margin-right: 0.5em"></Button>
                        </router-link>
                        <router-link :to="{ name: 'editVariantProductCollection', params: { uuid: slotProps.data.uuid }}">
                            <Button type="button" icon="pi pi-pencil" title="Éditer" class="p-edition" style="margin-right: 0.5em"></Button>
                        </router-link>
                        <Button @click="checkDeleteVariantProductCollection(slotProps.data)" type="button" title="Supprimer" icon="pi pi-trash" class="p-button-danger"></Button>
                        <a v-if="slotProps.data.hasChildren" :href="$router.resolve({name:'listCategories', params: {uuid: slotProps.data.uuid}}).href">
                            <Button type="button" icon="pi pi-eye" title="Voir les sous-catégories" class="p-button-success ml-2"></Button>
                        </a>
                    </template>
                </Column>
            </div>
            <template #empty>Aucune catégorie trouvée.</template>
        </DataTable>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import { useRoute } from "vue-router";
import ApiService from "@/_services/api.service";
import swal from "@/_helpers/swal";
import {
    fetchVariantProductCollection
} from "@/_services/grant/variantProductCollection";
import { ICategory } from "@/models/category/ICategory";

const route = useRoute();
const loading = ref<boolean>(true);
const categories: ICategory[] = [];
const tableData = ref<Array<ICategory>>(categories);
const uuidRouteParam = ref<string | null>(null);
const currentPage = ref<number>(0);
const rows = ref<number>(15);
const totalRecords = ref<number>(0);
const page = ref<number>(1);
const criteria = ref<string>("");
const modeSortable = ref<boolean>(false);
const categoryLabel = ref<string>("");
const title = ref<string>("Gérer les collections subventionnables");

onMounted(async () => {
    uuidRouteParam.value = route.params.uuid as string;
    await populateDatatable(rows.value, page.value);
});

const onFilterDt = () => {
    if (criteria.value.trim() != "") {
        populateDatatable(rows.value, page.value, criteria.value.trim());
    }
};

const onClearInputFilter = () => {
    if (criteria.value === "") {
        populateDatatable(rows.value, page.value);
    }
};

const onSort = (e) => {
  const sort = e.sortField
    ? {
      fieldLabel: `order[${e.sortField}]`,
      fieldValue: e.sortOrder === 1 ? 'asc' : 'desc',
    }
    : {};

  modeSortable.value = !!e.sortField;

  populateDatatable(rows.value, page.value, criteria.value, sort);
};

const onPaginate = async (e) => {
    rows.value = e.rows;
    currentPage.value = e.page;
    page.value = e.page + 1;
    populateDatatable(e.rows, e.page + 1, criteria.value);
};

const populateDatatable = async (itemsPerPage, page, criteria?, sort?) => {
    showSpinner();
    const [loadingDt, totalRecordsDt, tableDataDt] = await fetchVariantProductCollection(
        itemsPerPage,
        page,
        uuidRouteParam.value,
        criteria,
        sort
    );
    totalRecords.value = totalRecordsDt.value;
    loading.value = loadingDt.value;
    tableData.value = tableDataDt.value;
};

const checkDeleteVariantProductCollection = (category) => {
  swal
    .confirm(
      "Êtes-vous sûr de vouloir archiver la collection ?"
    )
    .then((result) => {
      if (result.isConfirmed) {
        deleteVariantProductCollection(category.uuid);
      }
    });
};

const deleteVariantProductCollection = async (uuidCategory) => {
    await ApiService.delete("admin/variant_product_collection/" + uuidCategory)
        .then(() => {
            swal
                .info("la collection a bien été archivée.")
                .then(() => {
                  populateDatatable(rows.value, page.value, criteria.value);
                });
        })
        .catch(() => {
            swal.error();
            return false;
        });
};

const showSpinner = () => {
    loading.value = true;
}

const hideSpinner = () => {
    loading.value = false;
}
</script>